/**
 * Small chip list editor: type a value and press Enter / comma / space (or
 * paste a list) to add it; × or Backspace on an empty field removes. Each value
 * is normalised and validated; invalid entries stay in the field with an error.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Input } from '../../ui/input';

interface ChipListInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  /** Return the normalised value, or null when invalid. */
  normalize: (raw: string) => string | null;
  invalidMessage: string;
  placeholder?: string;
  label: string;
  readOnly?: boolean;
  emptyText?: string;
  testId?: string;
}

export function ChipListInput({
  values,
  onChange,
  normalize,
  invalidMessage,
  placeholder,
  label,
  readOnly,
  emptyText,
  testId,
}: ChipListInputProps) {
  const { t } = useAppTranslation();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = (raw: string): boolean => {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return true;
    const next = [...values];
    const bad: string[] = [];
    for (const part of parts) {
      const v = normalize(part);
      if (!v) bad.push(part);
      else if (!next.includes(v)) next.push(v);
    }
    if (next.length !== values.length) onChange(next);
    if (bad.length) {
      setError(`${invalidMessage} (${bad.join(', ')})`);
      setDraft(bad.join(' '));
      return false;
    }
    setError(null);
    setDraft('');
    return true;
  };

  return (
    <div data-testid={testId}>
      <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {values.length === 0 && readOnly && (
          <span className="text-sm text-muted-foreground">{emptyText}</span>
        )}
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full border bg-slate-50 px-2 py-0.5 text-xs dark:border-slate-700 dark:bg-slate-800"
          >
            {v}
            {!readOnly && (
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onChange(values.filter((x) => x !== v))}
                aria-label={t('messaging.cloud.removeValue', 'Remove {{value}}', { value: v })}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      {!readOnly && (
        <Input
          className="mt-1.5"
          value={draft}
          aria-label={label}
          aria-invalid={!!error}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
              e.preventDefault();
              add(draft);
            } else if (e.key === 'Backspace' && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => draft.trim() && add(draft)}
          onPaste={(e) => {
            const text = e.clipboardData?.getData('text/plain') ?? '';
            if (/[\s,;]/.test(text.trim())) {
              e.preventDefault();
              add(text);
            }
          }}
        />
      )}
      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** "@Example.org" → "example.org"; null unless it's a syntactically valid domain. */
export function normalizeDomain(raw: string): string | null {
  const v = raw.trim().toLowerCase().replace(/^@/, '').replace(/\.$/, '');
  if (v.length > 253) return null;
  const label = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
  return new RegExp(`^(?:${label}\\.)+[a-z]{2,63}$`).test(v) ? v : null;
}

/** Microsoft tenant ID: a GUID, lower-cased. */
export function normalizeTenantId(raw: string): string | null {
  const v = raw.trim().toLowerCase().replace(/^\{|\}$/g, '');
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v) ? v : null;
}
